// Document.tsx
import React, { useState, useEffect } from 'react';
import PdfPreview from './ui/PDFPreview';
import Modal from './ui/Modal';
import { IconMaximize, IconX, IconRefresh } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { FullPdfViewer } from './ui/FullPDFViewer';

interface DocumentProps {
  document: {
    id: number;
    document_id: string;
    name: string | null;
    raw_document_presigned_url: string | null;
  };
  document_name: string;
  similarity_score: number;
  metadata: {
    text_characteristics: string[];
    non_text_characteristics: string[];
  };
}

const Document: React.FC<DocumentProps> = ({ document, document_name, similarity_score, metadata }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasUrl = Boolean(document.raw_document_presigned_url);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <div className="w-[20vw] relative">
      {/* Document Title + Expand Button */}
      <div className="flex flex-row justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800 truncate w-full">
          {document_name}
        </h3>

        {hasUrl && (
          <button
            onClick={openModal}
            className="bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
            aria-label="View full document"
          >
            <IconMaximize size={20} stroke={1.5} />
          </button>
        )}
      </div>

      {/* Small PDF Preview or No Preview Message */}
      {hasUrl ? (
        <div className="mb-4">
          <PdfPreview
            pdfUrl={document.raw_document_presigned_url!}
            width={300}
            height={400}
          />
        </div>
      ) : (
        <p className="text-sm text-gray-600 mb-4">No preview available. Document URL is missing.</p>
      )}

      {/* Similarity & Metadata */}
      <p className="text-sm text-gray-600">
        Similarity: {(similarity_score * 100).toFixed(1)}%
      </p>
      <p className="text-sm text-gray-600 h-16 overflow-y-scroll">
        Why? {metadata.text_characteristics.join(', ')} {metadata.non_text_characteristics.join(', ')}
      </p>

      {/* Modal for Full Document (All Pages) */}
      <Modal isOpen={isModalOpen} onRequestClose={closeModal}>
        <div className="bg-white flex flex-col items-center relative w-fit h-full overflow-y-auto z-100 rounded-md">
          {/* Controls in Top-Right */}
          <div className="absolute top-2 right-2 flex gap-2 z-110">
            <button
              onClick={closeModal}
              className="p-2 rounded-full hover:bg-gray-100"
              aria-label="Close modal"
            >
              <IconX size={24} stroke={1.5} />
            </button>
          </div>

          {/* PDF Full Viewer */}
          <div className="p-16">
            <h2 className="text-xl font-bold mb-4">{document_name}</h2>
            {hasUrl ? (
              <FullPdfViewer pdfUrl={document.raw_document_presigned_url!} />
            ) : (
              <p>No document available to view.</p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Document;
